import React from "react";
import { Box, Typography, Paper, Button, IconButton, Menu, MenuItem, TextField } from "@mui/material";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import { ChevronLeft, ChevronRight, KeyboardArrowDown } from "@mui/icons-material";
import { createPortal } from "react-dom";

const DatePickerPopover = ({
  anchorEl,
  open,
  onClose,
  currentMonth,
  setCurrentMonth,
  dateTab,
  setDateTab,
  departureExact,
  setDepartureExact,
  returnExact,
  setReturnExact,
  tripType,
  departureDate,
  setDepartureDate,
  returnDate,
  setReturnDate,
  handleDateSelect,
  isDateSelected,
  isDateInRange,
  generateCalendarDates,
  generatePriceForDate,
  getPriceColor,
  getPriceBackgroundColor,
  formatDate,
  showPrices = true,
  activeDateField = null,
  weekendSelectedDate = null,
  setWeekendSelectedDate = null,
  weekendDeparture = 'thursday',
  setWeekendDeparture = null,
    weekendReturn = 'sunday', 
  setWeekendReturn = null,
  isWeekendMode = false,
  setIsWeekendMode = null,
  monthDuration = '6-8',
  setMonthDuration = null,
  setIsMonthMode = null,
  isMonthMode = false
}) => {
  // Add hover state for range selection
  const [hoveredDate, setHoveredDate] = React.useState(null);

  // Add dropdown state
  const [departureDropdownAnchor, setDepartureDropdownAnchor] = React.useState(null);
  const [returnDropdownAnchor, setReturnDropdownAnchor] = React.useState(null);

  // Track viewport width for responsive behavior
  const [viewportWidth, setViewportWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

    // Weekend selection state is now managed by parent component
  
  // Month tab duration state
  const [localMonthDuration, setLocalMonthDuration] = React.useState(monthDuration || '6-8');
  const [durationInputValue, setDurationInputValue] = React.useState(monthDuration || '6-8');
  
  // Sync with parent component
  React.useEffect(() => {
    if (monthDuration) {
      setLocalMonthDuration(monthDuration);
      setDurationInputValue(monthDuration);
    }
  }, [monthDuration]);
  
  // Validate duration input (should be like "6-8", "5-7", "7-10", etc.)
  const validateDurationInput = (input) => {
    const pattern = /^\d{1,2}-\d{1,2}$/;
    if (!pattern.test(input)) return false;
    
    const [start, end] = input.split('-').map(Number);
    return start > 0 && end > 0 && start <= end && start <= 30 && end <= 30;
  };
  
  const handleDurationInputChange = (value) => {
    setDurationInputValue(value);
  };
  
  const handleDurationInputBlur = () => {
    if (validateDurationInput(durationInputValue)) {
      setLocalMonthDuration(durationInputValue);
      if (setMonthDuration) {
        setMonthDuration(durationInputValue);
      }
    } else {
      // Revert to default if invalid
      setDurationInputValue('6-8');
      setLocalMonthDuration('6-8');
      if (setMonthDuration) {
        setMonthDuration('6-8');
      }
    }
  };
  
  const handleDurationInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleDurationInputBlur();
    }
  };
  
  // Flexibility options
  const flexibilityOptions = [
    { value: 'exact', label: 'exact' },
    { value: 'day_after', label: '+ day after' },
    { value: 'day_before', label: '+ day before' },
    { value: 'plus_minus_1', label: '± 1 day' },
    { value: 'plus_minus_2', label: '± 2 days' },
    { value: 'plus_minus_3', label: '± 3 days' }
  ];

  // Weekend departure options
  const weekendDepartureOptions = [
    { value: 'thursday', label: 'Thursday' },
    { value: 'thu_am', label: 'Thu AM' },
    { value: 'thu_pm', label: 'Thu PM' },
    { value: 'friday', label: 'Friday' },
    { value: 'fri_am', label: 'Fri AM' },
    { value: 'fri_pm', label: 'Fri PM' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sat_am', label: 'Sat AM' },
    { value: 'sat_pm', label: 'Sat PM' }
  ];

  // Weekend return options  
  const weekendReturnOptions = [
    { value: 'sunday', label: 'Sunday' },
    { value: 'sun_am', label: 'Sun AM' },
    { value: 'sun_pm', label: 'Sun PM' },
    { value: 'monday', label: 'Monday' },
    { value: 'mon_am', label: 'Mon AM' },
    { value: 'mon_pm', label: 'Mon PM' }
  ];

  // Close dropdowns helper
  const closeDropdowns = () => {
    setDepartureDropdownAnchor(null);
    setReturnDropdownAnchor(null);
  };

  // Close dropdowns when date picker opens or when active field changes
  React.useEffect(() => {
    if (open) {
      closeDropdowns();
    }
  }, [open]);

  // Close dropdowns when switching between departure and return fields
  React.useEffect(() => {
    closeDropdowns();
  }, [activeDateField]);

  // Clear selected dates when switching tabs
  React.useEffect(() => {
    if (dateTab === 1) {
      // Clear dates when switching to weekend
      setDepartureDate('');
      setReturnDate('');
      // Set weekend mode
      if (setIsWeekendMode) {
        setIsWeekendMode(true);
      }
      // Clear month mode
      if (setIsMonthMode) {
        setIsMonthMode(false);
      }
      // Clear hover state to prevent range highlighting
      setHoveredDate(null);
    } else if (dateTab === 2) {
      // Clear return date when switching to month (keep departure date)
      setReturnDate('');
      // Clear weekend date when switching to month
      if (setWeekendSelectedDate) {
        setWeekendSelectedDate(null);
      }
      // Clear weekend mode
      if (setIsWeekendMode) {
        setIsWeekendMode(false);
      }
      // Set month mode
      if (setIsMonthMode) {
        setIsMonthMode(true);
      }
      // Clear hover state to prevent range highlighting
      setHoveredDate(null);
    } else if (dateTab === 0) {
      // Clear weekend date when switching to dates
      if (setWeekendSelectedDate) {
        setWeekendSelectedDate(null);
      }
      // Clear weekend mode
      if (setIsWeekendMode) {
        setIsWeekendMode(false);
      }
      // Clear month mode
      if (setIsMonthMode) {
        setIsMonthMode(false);
      }
      // Clear hover state to prevent range highlighting
      setHoveredDate(null);
    }
  }, [dateTab, setWeekendSelectedDate, setIsWeekendMode, setIsMonthMode]);

  // Add click outside handler
  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (anchorEl && !anchorEl.contains(event.target)) {
        // Check if click is outside the date picker and not in dropdown menus
        const datePickerElement = document.querySelector('[data-date-picker]');
        const dropdownMenus = document.querySelectorAll('[role="menu"]');

        let clickInDropdown = false;
        dropdownMenus.forEach(menu => {
          if (menu.contains(event.target)) {
            clickInDropdown = true;
          }
        });

        if (datePickerElement && !datePickerElement.contains(event.target) && !clickInDropdown) {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, anchorEl, onClose]);

  const handleMonthChange = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    
    // Prevent navigation to months earlier than the current month
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonthIndex = currentDate.getMonth();
    
    const newYear = newMonth.getFullYear();
    const newMonthIndex = newMonth.getMonth();
    
    // Check if the new month is earlier than the current month
    if (newYear < currentYear || (newYear === currentYear && newMonthIndex < currentMonthIndex)) {
      return; // Don't allow navigation to past months
    }
    
    setCurrentMonth(newMonth);
  };

  // Helper function to check if left navigation should be disabled
  const isLeftNavigationDisabled = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonthIndex = currentDate.getMonth();
    
    const currentYearValue = currentMonth.getFullYear();
    const currentMonthIndexValue = currentMonth.getMonth();
    
    // Disable if we're at the current month or earlier
    return currentYearValue < currentYear || (currentYearValue === currentYear && currentMonthIndexValue <= currentMonthIndex);
  };

  // Helper functions for date manipulation
  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  };

  // Format month header to show "Wed, 7/23" format
  const formatMonthHeader = (date) => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayOfWeek = dayNames[date.getDay()];
    return `${dayOfWeek}, ${month}/${day}`;
  };

  // Check if date is in hover range
  const isDateInHoverRange = (date) => {
    // In MONTH tab, don't show hover ranges
    if (dateTab === 2) return false;
    
    if (!hoveredDate || !departureDate || tripType === "One-way" || tripType === "Multi-city") return false;

    const dateTime = date.getTime();
    const departureTime = new Date(departureDate).getTime();
    const returnTime = returnDate ? new Date(returnDate).getTime() : null;
    const hoveredTime = hoveredDate.getTime();

    // Only show hover range if it makes logical sense
    if (activeDateField === "departure") {
      // When departure field is active, only show range if hover date is before or equal to return date
      if (returnTime && hoveredTime > returnTime) {
        return false; // Don't show range if hovering beyond return date
      }
      const start = Math.min(departureTime, hoveredTime);
      const end = Math.max(departureTime, hoveredTime);
      return dateTime >= start && dateTime <= end;
    } else if (activeDateField === "return") {
      // When return field is active, only show range if hover date is after or equal to departure date
      if (hoveredTime < departureTime) {
        return false; // Don't show range if hovering before departure date
      }
      const start = Math.min(returnTime || departureTime, hoveredTime);
      const end = Math.max(returnTime || departureTime, hoveredTime);
      return dateTime >= start && dateTime <= end;
    }

    // Default behavior for when no specific field is active
    const start = Math.min(departureTime, hoveredTime);
    const end = Math.max(departureTime, hoveredTime);
    return dateTime >= start && dateTime <= end;
  };

  const renderCalendar = (year, month) => {
    const days = generateCalendarDates(new Date(year, month));
    const weeks = [];

    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <Box>
        {weeks.map((week, weekIndex) => (
          <Box key={weekIndex} sx={{ display: 'flex', mb: 1 }}>
            {week.map((day, dayIndex) => {
              if (!day) {
                return <Box key={dayIndex} sx={{ flex: 1, p: 0.5 }} />;
              }

              const price = showPrices ? generatePriceForDate(day) : null;
              const isCurrentMonth = day.getMonth() === month;
              const isSelected = isDateSelected(day, dateTab);
              const isInRange = isDateInRange(day, dateTab);
              const isInHoverRange = isDateInHoverRange(day);
              const isPast = day < new Date().setHours(0, 0, 0, 0);
              const isToday = isSameDay(day, new Date());

              // Determine background color based on state
              let backgroundColor = 'transparent';
              let color = '#333';
              let borderRadius = '4px';
              let border = 'none';

              if (isSelected) {
                backgroundColor = '#333';
                color = '#fff';
                borderRadius = '4px';
              } else if (isInRange) {
                backgroundColor = '#333';
                color = '#fff';
                borderRadius = '4px';
              } else if (isInHoverRange) {
                backgroundColor = '#333';
                color = '#fff';
                borderRadius = '4px';
              } else if (isPast) {
                backgroundColor = 'transparent';
                color = '#ccc';
                borderRadius = '4px';
              } else if (isToday) {
                backgroundColor = 'transparent';
                color = '#333';
                border = 'none';
                borderRadius = '4px';
              } else if (isCurrentMonth && !isPast && showPrices && price && dateTab === 0 && tripType !== "Multi-city") {
                // Use ONLY the exact colors you provided - only show in DATES tab and not for Multi-city
                if (price <= 1150) {
                  backgroundColor = '#81dfb0'; // Green for $1150+
                } else if (price <= 1302) {
                  backgroundColor = '#ffc470'; // Orange for $1302+
                } else {
                  backgroundColor = '#fb9da2'; // Pink for $1472+
                }
                color = '#333';
                borderRadius = '4px';
              }

              return (
                <Box key={dayIndex} sx={{ flex: 1, textAlign: 'center', p: 0.5 }}>
                  <Button
                    onClick={() => {
                      if (!isPast) {
                        closeDropdowns();
                        if (dateTab === 0) {
                          // DATES tab - normal date selection
                          handleDateSelect(day);
                        } else if (dateTab === 1) {
                          // WEEKEND tab - select date and close dialog
                          if (setWeekendSelectedDate) {
                            setWeekendSelectedDate(day);
                          }
                          onClose(); // Close the entire date picker
                        } else if (dateTab === 2) {
                          // MONTH tab - select departure date and close dialog
                          setDepartureDate(day);
                          onClose(); // Close the entire date picker
                        }
                      }
                    }}
                    onMouseEnter={() => !isPast && setHoveredDate(day)}
                    onMouseLeave={() => setHoveredDate(null)}
                    disabled={isPast}
                    sx={{
                      width: 40,
                      height: 40,
                      minWidth: 40,
                      borderRadius: borderRadius,
                      backgroundColor: backgroundColor,
                      color: color,
                      fontWeight: isSelected ? 700 : 600,
                      fontSize: '16px',
                      lineHeight: '24px',
                      border: border,
                      cursor: (dateTab === 0 || dateTab === 1 || dateTab === 2) && !isPast ? 'pointer' : 'default',
                      '&:hover': {
                        backgroundColor: (dateTab === 0 || dateTab === 1 || dateTab === 2) && !isPast ? (
                          isSelected ? '#555' :
                            (showPrices && price && isCurrentMonth && dateTab === 0 && tripType !== "Multi-city") ? '#333' :
                              '#f0f0f0'
                        ) : backgroundColor,
                        color: (dateTab === 0 || dateTab === 1 || dateTab === 2) && !isPast ? (
                          isSelected ? '#fff' :
                            (showPrices && price && isCurrentMonth && !isPast && dateTab === 0 && tripType !== "Multi-city") ? '#fff' :
                              '#333'
                        ) : color,
                      },
                      '&:disabled': {
                        color: '#ccc',
                        backgroundColor: 'transparent',
                        border: 'none'
                      },
                      mx: 0,
                      my: 0,
                      p: 0,
                      minWidth: 0,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {day.getDate()}
                  </Button>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    );
  };

  if (!open || !anchorEl) {
    return null;
  }

  // Responsive width for smaller screens
  const responsiveWidth = viewportWidth < 900 ? Math.min(viewportWidth - 20, 800) : 800;

  const datePickerContent = (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      modifiers={[
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'preventOverflow', options: { padding: 8 } },
        { name: 'flip', options: { fallbackPlacements: ['top-end', 'bottom-start'] } },
      ]}
      transition
      style={{ zIndex: 1300 }}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
          <Paper
            data-date-picker
            onClick={(e) => e.stopPropagation()}
            sx={{
              borderRadius: 2,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              p: 0,
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              width: `${responsiveWidth}px`,
              overflow: 'hidden',
              maxHeight: '600px'
            }}
          >
      {/* Hide all filters for Multi-city */}
      {tripType !== "Multi-city" && (
        <Box sx={{ p: 2, borderBottom: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Only show tabs for Round-trip */}
          {tripType !== "One-way" && (
            <Box sx={{ display: "flex", gap: 4 }}>
              <Typography
                onClick={() => setDateTab(0)}
                sx={{
                  fontWeight: dateTab === 0 ? 600 : 500,
                  fontSize: "12px",
                  lineHeight: "18px",
                  color: dateTab === 0 ? "#333" : "#666",
                  pb: 1,
                  borderBottom: dateTab === 0 ? "2px solid #333" : "none",
                  cursor: "pointer",
                }}
              >
                DATES
              </Typography>
              <Typography
                onClick={() => setDateTab(1)}
                sx={{
                  fontWeight: dateTab === 1 ? 600 : 500,
                  fontSize: "12px",
                  lineHeight: "18px",
                  color: dateTab === 1 ? "#333" : "#666",
                  pb: 1,
                  borderBottom: dateTab === 1 ? "2px solid #333" : "none",
                  cursor: "pointer",
                }}
              >
                WEEKEND
              </Typography>
              <Typography
                onClick={() => setDateTab(2)}
                sx={{
                  fontWeight: dateTab === 2 ? 600 : 500,
                  fontSize: "12px",
                  lineHeight: "18px",
                  color: dateTab === 2 ? "#333" : "#666",
                  pb: 1,
                  borderBottom: dateTab === 2 ? "2px solid #333" : "none",
                  cursor: "pointer",
                }}
              >
                MONTH
              </Typography>
            </Box>
          )}
          
          {/* For One-way, show empty space where tabs would be */}
          {tripType === "One-way" && <Box />}
          <Box sx={{ display: "flex", gap: 1 }}>
            {(dateTab === 0 || tripType === "One-way") ? (
              // DATES tab or One-way - show departure dropdown (and return for round-trip)
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: "14px", color: "#333" }}>
                    Departure
                  </Typography>
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDepartureDropdownAnchor(departureDropdownAnchor ? null : e.currentTarget);
                    }}
                    endIcon={<KeyboardArrowDown sx={{ fontSize: '16px' }} />}
                    sx={{
                      fontSize: "12px",
                      lineHeight: "18px",
                      color: "#1976d2",
                      fontWeight: 500,
                      textTransform: "none",
                      minWidth: "auto",
                      padding: "2px 4px",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {flexibilityOptions.find(opt => opt.value === departureExact)?.label || 'exact'}
                  </Button>
                </Box>

                {tripType !== "One-way" && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: "14px", color: "#333" }}>
                      Return
                    </Typography>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setReturnDropdownAnchor(returnDropdownAnchor ? null : e.currentTarget);
                      }}
                      endIcon={<KeyboardArrowDown sx={{ fontSize: '16px' }} />}
                      sx={{
                        fontSize: "12px",
                        lineHeight: "18px",
                        color: "#1976d2",
                        fontWeight: 500,
                        textTransform: "none",
                        minWidth: "auto",
                        padding: "2px 4px",
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      {flexibilityOptions.find(opt => opt.value === returnExact)?.label || 'exact'}
                    </Button>
                  </Box>
                )}
              </>
            ) : dateTab === 1 ? (
              // WEEKEND tab - show departure/return weekend dropdowns
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: "14px", color: "#333" }}>
                    Departure
                  </Typography>
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDepartureDropdownAnchor(departureDropdownAnchor ? null : e.currentTarget);
                    }}
                    endIcon={<KeyboardArrowDown sx={{ fontSize: '16px' }} />}
                    sx={{
                      fontSize: "12px",
                      lineHeight: "18px",
                      color: "#1976d2",
                      fontWeight: 500,
                      textTransform: "none",
                      minWidth: "auto",
                      padding: "2px 4px",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {weekendDepartureOptions.find(opt => opt.value === weekendDeparture)?.label || 'Thursday'}
                  </Button>
                </Box>

                {tripType !== "One-way" && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: "14px", color: "#333" }}>
                      Return
                    </Typography>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setReturnDropdownAnchor(returnDropdownAnchor ? null : e.currentTarget);
                      }}
                      endIcon={<KeyboardArrowDown sx={{ fontSize: '16px' }} />}
                      sx={{
                        fontSize: "12px",
                        lineHeight: "18px",
                        color: "#1976d2",
                        fontWeight: 500,
                        textTransform: "none",
                        minWidth: "auto",
                        padding: "2px 4px",
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      {weekendReturnOptions.find(opt => opt.value === weekendReturn)?.label || 'Mon AM'}
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              // MONTH tab - show duration input
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: "14px", color: "#333" }}>
                    Duration
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={durationInputValue}
                      onChange={(e) => handleDurationInputChange(e.target.value)}
                      onBlur={handleDurationInputBlur}
                      onKeyPress={handleDurationInputKeyPress}
                      placeholder="6-8"
                      sx={{
                        width: '60px',
                        '& .MuiOutlinedInput-root': {
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#333',
                          textAlign: 'center',
                          '& input': {
                            textAlign: 'center',
                            padding: '6px 8px'
                          }
                        }
                      }}
                    />
                    <Typography sx={{ fontSize: "14px", color: "#333", fontWeight: 500 }}>
                      nights
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "12px", color: "#666", ml: 1 }}>
                    Select earliest departure date
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {dateTab === 0 && (
        // DATES tab - show regular calendar
        <Box sx={{ display: "flex", p: 2 }}>
          {/* Left Calendar */}
          <Box sx={{ flex: 1, pr: 2,  border: "none"}}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <IconButton 
                onClick={() => handleMonthChange(-1)} 
                size="small"
                disabled={isLeftNavigationDisabled()}
                sx={{ 
                  opacity: isLeftNavigationDisabled() ? 0.3 : 1,
                  cursor: isLeftNavigationDisabled() ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#333" }}>
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Typography>
              <IconButton onClick={() => handleMonthChange(1)} size="small" sx={{ visibility: 'hidden' }}>
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Week headers */}
            <Box sx={{ display: "flex", mb: 1 }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <Box key={index} sx={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#666',
                  py: 1
                }}>
                  {day}
                </Box>
              ))}
            </Box>

            {/* Calendar */}
            {renderCalendar(currentMonth.getFullYear(), currentMonth.getMonth())}
          </Box>

          {/* Right Calendar */}
          <Box sx={{ flex: 1, pl: tripType === "Multi-city" ? 0 : 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <IconButton 
                onClick={() => handleMonthChange(-1)} 
                size="small" 
                disabled={isLeftNavigationDisabled()}
                sx={{ 
                  visibility: 'hidden',
                  opacity: isLeftNavigationDisabled() ? 0.3 : 1,
                  cursor: isLeftNavigationDisabled() ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#333" }}>
                {new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Typography>
              <IconButton onClick={() => handleMonthChange(1)} size="small">
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Week headers */}
            <Box sx={{ display: "flex", mb: 1 }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <Box key={index} sx={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#666',
                  py: 1
                }}>
                  {day}
                </Box>
              ))}
            </Box>

            {/* Calendar */}
            {renderCalendar(currentMonth.getFullYear(), currentMonth.getMonth() + 1)}
          </Box>
        </Box>
      )}

      {dateTab === 1 && (
        // WEEKEND tab - show calendar view
        <Box sx={{ display: "flex", p: 2 }}>
          {/* Left Calendar */}
          <Box sx={{ flex: 1, pr: tripType === "Multi-city" ? 0 : 2, borderRight: "none" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <IconButton 
                onClick={() => handleMonthChange(-1)} 
                size="small"
                disabled={isLeftNavigationDisabled()}
                sx={{ 
                  opacity: isLeftNavigationDisabled() ? 0.3 : 1,
                  cursor: isLeftNavigationDisabled() ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#333" }}>
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Typography>
              <IconButton onClick={() => handleMonthChange(1)} size="small" sx={{ visibility: 'hidden' }}>
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Week headers */}
            <Box sx={{ display: "flex", mb: 1 }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <Box key={index} sx={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#666',
                  py: 1
                }}>
                  {day}
                </Box>
              ))}
            </Box>

            {/* Calendar */}
            {renderCalendar(currentMonth.getFullYear(), currentMonth.getMonth())}
          </Box>

          {/* Right Calendar */}
          <Box sx={{ flex: 1, pl: tripType === "Multi-city" ? 0 : 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <IconButton 
                onClick={() => handleMonthChange(-1)} 
                size="small" 
                disabled={isLeftNavigationDisabled()}
                sx={{ 
                  visibility: 'hidden',
                  opacity: isLeftNavigationDisabled() ? 0.3 : 1,
                  cursor: isLeftNavigationDisabled() ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#333" }}>
                {new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Typography>
              <IconButton onClick={() => handleMonthChange(1)} size="small">
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Week headers */}
            <Box sx={{ display: "flex", mb: 1 }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <Box key={index} sx={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#666',
                  py: 1
                }}>
                  {day}
                </Box>
              ))}
            </Box>

            {/* Calendar */}
            {renderCalendar(currentMonth.getFullYear(), currentMonth.getMonth() + 1)}
          </Box>
        </Box>
      )}

      {dateTab === 2 && (
        // MONTH tab - show calendar view
        <Box sx={{ display: "flex", p: 2 }}>
          {/* Left Calendar */}
          <Box sx={{ flex: 1, pr: tripType === "Multi-city" ? 0 : 2, borderRight: tripType === "Multi-city" ? "none" : "1px solid #f0f0f0" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <IconButton 
                onClick={() => handleMonthChange(-1)} 
                size="small"
                disabled={isLeftNavigationDisabled()}
                sx={{ 
                  opacity: isLeftNavigationDisabled() ? 0.3 : 1,
                  cursor: isLeftNavigationDisabled() ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#333" }}>
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Typography>
              <IconButton onClick={() => handleMonthChange(1)} size="small" sx={{ visibility: 'hidden' }}>
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Week headers */}
            <Box sx={{ display: "flex", mb: 1 }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <Box key={index} sx={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#666',
                  py: 1
                }}>
                  {day}
                </Box>
              ))}
            </Box>

            {/* Calendar */}
            {renderCalendar(currentMonth.getFullYear(), currentMonth.getMonth())}
          </Box>

          {/* Right Calendar */}
          <Box sx={{ flex: 1, pl: tripType === "Multi-city" ? 0 : 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <IconButton 
                onClick={() => handleMonthChange(-1)} 
                size="small" 
                disabled={isLeftNavigationDisabled()}
                sx={{ 
                  visibility: 'hidden',
                  opacity: isLeftNavigationDisabled() ? 0.3 : 1,
                  cursor: isLeftNavigationDisabled() ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#333" }}>
                {new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Typography>
              <IconButton onClick={() => handleMonthChange(1)} size="small">
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Week headers */}
            <Box sx={{ display: "flex", mb: 1 }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <Box key={index} sx={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#333',
                  py: 1
                }}>
                  {day}
                </Box>
              ))}
            </Box>

            {/* Calendar */}
            {renderCalendar(currentMonth.getFullYear(), currentMonth.getMonth() + 1)}
          </Box>
        </Box>
      )}

      {/* Price Legend - only show for DATES tab and not for Multi-city */}
      {showPrices && dateTab === 0 && tripType !== "Multi-city" && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          border: "none"

        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, minWidth: "72px", backgroundColor: '#81dfb0', padding: "8px 0px", borderRadius: "4px" }}>

            <Typography sx={{ fontSize: '13px', fontWeight: '600', color: '#256847' }}>
              $1150+
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, minWidth: "72px", backgroundColor: '#ffc470', padding: "8px 0px", borderRadius: "4px" }}>

            <Typography sx={{ fontSize: '13px', fontWeight: '600', color: '#6f3205' }}>
              $1302+
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, minWidth: "72px", backgroundColor: '#fb9da2', padding: "8px 0px", borderRadius: "4px" }}>

            <Typography sx={{ fontSize: '13px', fontWeight: '600', color: '#802028' }}>
              $1472+
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '12px', color: '#333333', ml: 1 }}>
            Estimated prices for round-trip flights
          </Typography>
        </Box>
      )}

          </Paper>
        </Grow>
      )}
    </Popper>
  );

  // Render dropdown menus separately as portals to ensure highest z-index
  const departureDropdown = Boolean(departureDropdownAnchor) && createPortal(
    <Menu
      anchorEl={departureDropdownAnchor}
      open={Boolean(departureDropdownAnchor)}
      onClose={(e) => {
        e?.stopPropagation();
        setDepartureDropdownAnchor(null);
      }}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          minWidth: "140px",
          mt: 1,
          zIndex: 15000,
        },
      }}
      slotProps={{
        root: {
          style: {
            zIndex: 15000
          }
        }
      }}
      MenuListProps={{
        sx: {
          py: 0.5
        }
      }}
    >
      {(dateTab === 1 ? weekendDepartureOptions : flexibilityOptions).map((option) => (
        <MenuItem
          key={option.value}
          onClick={(e) => {
            e.stopPropagation();
            if (dateTab === 1) {
              if (setWeekendDeparture) {
                setWeekendDeparture(option.value);
              }
            } else {
              setDepartureExact(option.value);
            }
            setDepartureDropdownAnchor(null);
          }}
          sx={{
            fontSize: "12px",
            lineHeight: "18px",
            backgroundColor: (dateTab === 1 ? weekendDeparture === option.value : departureExact === option.value) ? "#e3f2fd" : "transparent",
            fontWeight: (dateTab === 1 ? weekendDeparture === option.value : departureExact === option.value) ? 600 : 400,
            "&:hover": {
              backgroundColor: (dateTab === 1 ? weekendDeparture === option.value : departureExact === option.value) ? "#e3f2fd" : "#f8f9fa",
            },
          }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Menu>,
    document.body
  );

  const returnDropdown = Boolean(returnDropdownAnchor) && createPortal(
    <Menu
      anchorEl={returnDropdownAnchor}
      open={Boolean(returnDropdownAnchor)}
      onClose={(e) => {
        e?.stopPropagation();
        setReturnDropdownAnchor(null);
      }}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          minWidth: "140px",
          mt: 1,
          zIndex: 15000,
        },
      }}
      slotProps={{
        root: {
          style: {
            zIndex: 15000
          }
        }
      }}
      MenuListProps={{
        sx: {
          py: 0.5
        }
      }}
    >
      {(dateTab === 1 ? weekendReturnOptions : flexibilityOptions).map((option) => (
        <MenuItem
          key={option.value}
          onClick={(e) => {
            e.stopPropagation();
            if (dateTab === 1) {
              if (setWeekendReturn) {
                setWeekendReturn(option.value);
              }
            } else {
              setReturnExact(option.value);
            }
            setReturnDropdownAnchor(null);
          }}
          sx={{
            fontSize: "12px",
            lineHeight: "18px",
            backgroundColor: (dateTab === 1 ? weekendReturn === option.value : returnExact === option.value) ? "#e3f2fd" : "transparent",
            fontWeight: (dateTab === 1 ? weekendReturn === option.value : returnExact === option.value) ? 600 : 400,
            "&:hover": {
              backgroundColor: (dateTab === 1 ? weekendReturn === option.value : returnExact === option.value) ? "#e3f2fd" : "#f8f9fa",
            },
          }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Menu>,
    document.body
  );

  return (
    <>
      {createPortal(datePickerContent, document.body)}
      {departureDropdown}
      {returnDropdown}
    </>
  );
};

export default DatePickerPopover; 